import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useSalesforce } from '../hooks/useSalesforce';
import Navbar from '../components/Navbar';
import { supabase } from '../services/supabaseClient';
import { TransferOrgModal } from '../components/TransferOrgModal';
import { PendingTransfer } from '../components/PendingTransfer';
import { ReauthModal } from '../components/ReauthModal';

const DEFAULT_PROMPT = `Analyze this Salesforce Flow and provide a comprehensive analysis.

Please provide your analysis in the following format with these exact section headers:

**Purpose:** Brief explanation of what this flow accomplishes from a business perspective.

**Flow Summary:** Comprehensive overview of the flow's logic, including when it triggers, the overall execution path, and how it achieves its purpose.

**Actions:** List the key actions performed by this flow in order of execution. Use bullet points or numbered list.

**Key Decision Points:** Describe any conditional logic, branching, or decision elements that affect the flow's execution path.

**Objects/Records Modified:** List all Salesforce objects that are created, read, updated, or deleted by this flow.

Keep the analysis clear and business-focused while being technically accurate.`;

export default function OrgSettingsPage() {
  const { orgId } = useParams<{ orgId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { orgs } = useSalesforce();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [org, setOrg] = useState<any>(null);
  const [customPrompt, setCustomPrompt] = useState('');
  const [useCustomPrompt, setUseCustomPrompt] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const [showReauthModal, setShowReauthModal] = useState(false);
  const [isEditingOrgName, setIsEditingOrgName] = useState(false);
  const [editedOrgName, setEditedOrgName] = useState('');
  const [savingOrgName, setSavingOrgName] = useState(false);

  useEffect(() => {
    loadOrgData();
  }, [orgId]);

  const loadOrgData = async () => {
    if (!orgId) return;
    
    try {
      // Get org details
      const { data: orgData, error } = await supabase
        .from('sf_orgs')
        .select('*')
        .eq('id', orgId)
        .single();
        
      if (error) throw error;
      
      setOrg(orgData);
      if (orgData.custom_flow_analysis_prompt) {
        setCustomPrompt(orgData.custom_flow_analysis_prompt);
        setUseCustomPrompt(true);
      } else {
        setCustomPrompt(DEFAULT_PROMPT);
      }
    } catch (error) {
      console.error('Error loading org data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSavePrompt = async () => {
    if (!orgId) return;
    
    setSaving(true);
    setSaveSuccess(false);
    
    try {
      const { error } = await supabase
        .from('sf_orgs')
        .update({
          custom_flow_analysis_prompt: useCustomPrompt ? customPrompt : null
        })
        .eq('id', orgId);
        
      if (error) throw error;
      
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Error saving prompt:', error);
      alert('Failed to save prompt. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleResetPrompt = () => {
    setCustomPrompt(DEFAULT_PROMPT);
  };

  const handleSaveOrgName = async () => {
    if (!editedOrgName.trim() || editedOrgName === org.name) {
      setIsEditingOrgName(false);
      return;
    }

    setSavingOrgName(true);
    try {
      const { error } = await supabase
        .from('sf_orgs')
        .update({ name: editedOrgName.trim() })
        .eq('id', orgId);

      if (error) throw error;

      setOrg({ ...org, name: editedOrgName.trim() });
      setIsEditingOrgName(false);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Error updating org name:', error);
      alert('Failed to update organization name. Please try again.');
    } finally {
      setSavingOrgName(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100">
        <Navbar />
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Loading...</div>
        </div>
      </div>
    );
  }

  if (!org) {
    return (
      <div className="min-h-screen bg-gray-100">
        <Navbar />
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Organization not found</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <Navbar />
      
      <div className="py-10">
        <header>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold leading-tight text-gray-900">
                  Organization Settings
                </h1>
              </div>
              {/* Back to Flows button - Hidden for now
              <button
                onClick={() => navigate(`/${orgId}/flows`)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                Back to Flows
              </button>
              */}
            </div>
          </div>
        </header>
        
        <main>
          <div className="max-w-7xl mx-auto sm:px-6 lg:px-8">
            <div className="px-4 py-8 sm:px-0">
              {/* AI Analysis Prompt Settings - Hidden for now
              <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                <div className="px-4 py-5 sm:px-6">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    AI Flow Analysis Settings
                  </h3>
                  <p className="mt-1 max-w-2xl text-sm text-gray-500">
                    Customize how AI analyzes flows for this organization
                  </p>
                </div>
                
                {saveSuccess && (
                  <div className="bg-green-50 p-4 mx-6">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-green-800">Settings saved successfully</p>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
                  <div className="space-y-6">
                    <div className="bg-white p-4 rounded-md border border-gray-200">
                      <div className="flex items-center">
                        <input
                          id="useCustomPrompt"
                          type="checkbox"
                          checked={useCustomPrompt}
                          onChange={(e) => setUseCustomPrompt(e.target.checked)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded bg-white"
                        />
                        <label htmlFor="useCustomPrompt" className="ml-2 block text-sm font-medium text-gray-700">
                          Use custom analysis prompt
                        </label>
                      </div>
                      <p className="mt-1 text-sm text-gray-500">
                        Override the default AI prompt used to analyze flows in this organization
                      </p>
                    </div>
                    
                    {useCustomPrompt && (
                      <div>
                        <label htmlFor="customPrompt" className="block text-sm font-medium text-gray-700">
                          Custom Prompt Template
                        </label>
                        <div className="mt-1">
                          <textarea
                            id="customPrompt"
                            rows={15}
                            value={customPrompt}
                            onChange={(e) => setCustomPrompt(e.target.value)}
                            className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border border-gray-300 rounded-md font-mono text-xs bg-white text-gray-900 p-3"
                            placeholder="Enter your custom prompt template..."
                          />
                        </div>
                        <div className="mt-2 flex justify-between items-center">
                          <p className="text-sm text-gray-500">
                            Use markdown formatting. The AI will receive flow metadata along with this prompt.
                          </p>
                          <button
                            type="button"
                            onClick={handleResetPrompt}
                            className="inline-flex items-center px-3 py-1 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                          >
                            Reset to Default
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="px-4 py-3 bg-gray-50 text-right sm:px-6">
                  <button
                    type="button"
                    onClick={handleSavePrompt}
                    disabled={saving}
                    className={`inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white ${
                      saving ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'
                    } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
                  >
                    {saving ? 'Saving...' : 'Save Settings'}
                  </button>
                </div>
              </div>
              */}
              
              {/* Other org settings can be added here */}
              <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
                  <div>
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Organization Information
                    </h3>
                  </div>
                  <button
                    onClick={() => setShowReauthModal(true)}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Re-authenticate
                  </button>
                </div>
                <div className="border-t border-gray-200">
                  <dl>
                    <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                      <dt className="text-sm font-medium text-gray-500">Organization Name</dt>
                      <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                        {isEditingOrgName ? (
                          <div className="flex items-center space-x-2">
                            <input
                              type="text"
                              value={editedOrgName}
                              onChange={(e) => setEditedOrgName(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  handleSaveOrgName();
                                } else if (e.key === 'Escape') {
                                  setIsEditingOrgName(false);
                                  setEditedOrgName(org.name);
                                }
                              }}
                              className="flex-1 px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              autoFocus
                            />
                            <button
                              onClick={handleSaveOrgName}
                              disabled={savingOrgName}
                              className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                            >
                              {savingOrgName ? 'Saving...' : 'Save'}
                            </button>
                            <button
                              onClick={() => {
                                setIsEditingOrgName(false);
                                setEditedOrgName(org.name);
                              }}
                              className="inline-flex items-center px-3 py-1 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center space-x-2">
                            <span>{org.name}</span>
                            <button
                              onClick={() => {
                                setIsEditingOrgName(true);
                                setEditedOrgName(org.name);
                              }}
                              className="inline-flex items-center p-1 border border-transparent rounded-md text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                              title="Edit organization name"
                            >
                              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                              </svg>
                            </button>
                          </div>
                        )}
                      </dd>
                    </div>
                    <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                      <dt className="text-sm font-medium text-gray-500">Instance URL</dt>
                      <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{org.instance_url}</dd>
                    </div>
                    <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                      <dt className="text-sm font-medium text-gray-500">Environment</dt>
                      <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                        {org.is_sandbox ? 'Sandbox' : 'Production'}
                      </dd>
                    </div>
                    <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                      <dt className="text-sm font-medium text-gray-500">Last Synced</dt>
                      <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                        {org.last_synced_at ? new Date(org.last_synced_at).toLocaleString() : 'Never'}
                      </dd>
                    </div>
                  </dl>
                </div>
              </div>
              
              {/* Pending Transfer Alert */}
              <PendingTransfer 
                org={org} 
                onTransferCancelled={() => {
                  // Reload org data to refresh the UI
                  loadOrgData();
                }}
              />
              
              {/* Transfer Organization Section */}
              <div className="mt-8 bg-white shadow overflow-hidden sm:rounded-lg">
                <div className="px-4 py-5 sm:px-6">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Transfer Organization
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Transfer this organization to another user account
                  </p>
                </div>
                <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
                  <p className="text-sm text-gray-600 mb-4">
                    Transfer ownership of this organization and all its flow documentation to another user. 
                    The recipient will need to authenticate with their own Salesforce credentials.
                  </p>
                  <button
                    onClick={() => setShowTransferModal(true)}
                    disabled={!!org.transfer_token}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                    </svg>
                    Transfer Organization
                  </button>
                </div>
              </div>
              
              {/* Danger Zone */}
              <div className="mt-8 bg-white shadow overflow-hidden sm:rounded-lg border-2 border-red-200">
                <div className="bg-red-50 px-4 py-5 sm:px-6">
                  <h3 className="text-lg leading-6 font-medium text-red-900">
                    Danger Zone
                  </h3>
                </div>
                <div className="border-t border-red-200 px-4 py-5 sm:px-6">
                  <div className="space-y-4">
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">Disconnect Organization</h4>
                      <p className="mt-1 text-sm text-gray-600">
                        Remove this organization from your account. This will delete all cached flow data but will not affect your Salesforce org.
                      </p>
                      <button
                        onClick={() => setShowDisconnectConfirm(true)}
                        className="mt-3 inline-flex items-center px-4 py-2 border border-red-300 rounded-md shadow-sm text-sm font-medium text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                      >
                        Disconnect Organization
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
      
      {/* Transfer Modal */}
      {showTransferModal && org && (
        <TransferOrgModal
          org={org}
          isOpen={showTransferModal}
          onClose={() => setShowTransferModal(false)}
          onSuccess={() => {
            setShowTransferModal(false);
            navigate('/orgs');
          }}
        />
      )}
      
      {/* Disconnect Confirmation Modal */}
      {showDisconnectConfirm && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Disconnect Organization?</h3>
            <p className="text-sm text-gray-600 mb-6">
              This will remove "{org?.name}" from your account and delete all cached flow data. 
              This action cannot be undone, but you can reconnect the org later.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDisconnectConfirm(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  // TODO: Implement disconnect functionality
                  console.log('Disconnect org:', orgId);
                  setShowDisconnectConfirm(false);
                  navigate('/orgs');
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700"
              >
                Disconnect
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Re-authentication Modal */}
      {showReauthModal && org && (
        <ReauthModal
          isOpen={showReauthModal}
          onClose={() => setShowReauthModal(false)}
          orgId={org.id}
          orgName={org.name}
        />
      )}
    </div>
  );
}